import { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Description, Label, Modal, toast } from '@heroui/react';
import { CheckboxButtonGroup } from '@heroui-pro/react';
import { useEnums } from '@hs/shared';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

interface SkuAppAddModalProps {
  open: boolean;
  skuId: string;
  skuName?: string;
  /** 当前 SKU 已关联的 appId 列表 */
  existingAppIds: string[];
  onCancel: () => void;
  onSuccess: () => void;
}

export function SkuAppAddModal({
  open,
  skuId,
  skuName,
  existingAppIds,
  onCancel,
  onSuccess,
}: SkuAppAddModalProps) {
  const { getLabel } = useEnums({ trpc: trpc as any, modules: ['app'] });
  const utils = trpc.useUtils();
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);

  const { data: appsData, isLoading } = trpc.bizApp.listGrouped.useQuery(
    { groupBy: 'type', status: 'ACTIVE' },
    { enabled: open }
  );

  useEffect(() => {
    if (!open) setSelectedAppIds([]);
  }, [open]);

  const createManyMutation = trpc.bizProduct.skuApp.createMany.useMutation({
    onSuccess: () => {
      toast.success('应用已添加');
      void utils.bizProduct.skuApp.listAll.invalidate();
      onSuccess();
    },
    onError: (error) => {
      toast.danger('添加失败', { description: error.message });
    },
  });

  const appGroups = useMemo(() => {
    const groups = (appsData && 'groups' in appsData ? appsData.groups : []) ?? [];
    const existingSet = new Set(existingAppIds);
    return groups
      .map((group) => {
        const key = typeof group.key === 'string' ? group.key : String(group.key);
        return {
          key,
          items: (group.items ?? []).filter((app) => {
            const appId = app.id ? String(app.id) : '';
            return Boolean(appId) && !existingSet.has(appId);
          }),
        };
      })
      .filter((group) => group.items.length > 0);
  }, [appsData, existingAppIds]);

  const availableCount = useMemo(
    () => appGroups.reduce((sum, g) => sum + g.items.length, 0),
    [appGroups]
  );

  const handleSubmit = async () => {
    if (selectedAppIds.length === 0) {
      toast.warning('请先选择要添加的应用');
      return;
    }
    await createManyMutation.mutateAsync({
      skuId,
      appIds: selectedAppIds,
      status: 'ACTIVE',
    });
  };

  const isSaving = createManyMutation.isPending;

  return (
    <Modal isOpen={open} onOpenChange={(next) => !next && onCancel()}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside">
          <Modal.Dialog className="min-h-[50vh] min-w-[50vw] max-h-[90vh] w-full max-w-[90vw]">
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading>添加应用</Modal.Heading>
              <Description className="text-sm">SKU：{skuName || '-'}</Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <div className="max-h-[60vh] min-h-0 overflow-y-auto p-2">
                {isLoading ? (
                  <p className="py-10 text-center text-sm text-muted">加载中…</p>
                ) : availableCount > 0 ? (
                  <CheckboxButtonGroup
                    className="grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                    layout="grid"
                    name="sku-apps-add-select"
                    value={selectedAppIds}
                    onChange={setSelectedAppIds}
                    variant="secondary"
                  >
                    {appGroups.map((group) => {
                      const groupLabel =
                        getLabel('app', 'type', group.key) || group.key;
                      return (
                        <Fragment key={group.key}>
                          <Label className="col-span-full text-base font-semibold">
                            {groupLabel}
                          </Label>
                          {group.items.map((app) => {
                            const appId = String(app.id);
                            return (
                              <CheckboxButtonGroup.Item key={appId} value={appId}>
                                <CheckboxButtonGroup.Indicator />
                                <CheckboxButtonGroup.ItemContent>
                                  <div className="flex flex-col gap-1">
                                    <Label>{app.name}</Label>
                                    {app.description ? (
                                      <Description className="line-clamp-2 text-xs">
                                        {app.description}
                                      </Description>
                                    ) : null}
                                  </div>
                                </CheckboxButtonGroup.ItemContent>
                              </CheckboxButtonGroup.Item>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </CheckboxButtonGroup>
                ) : (
                  <p className="py-10 text-center text-sm text-muted">
                    当前没有可添加的应用
                  </p>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                variant="primary"
                isDisabled={isSaving || selectedAppIds.length === 0}
                onPress={() => void handleSubmit()}
              >
                {isSaving ? '保存中…' : '保存'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
